import { useEffect, useState, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import Cookie from "js-cookie";
import authStore from "../../auth/AuthStore";

const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const PING_INTERVAL = 30000; // 30 seconds - keeps connection alive through proxies

// Connection status values:
// - 'disconnected': Not connected
// - 'connecting': Initial connection attempt
// - 'connected': Successfully connected
// - 'reconnecting': Attempting to reconnect after disconnect
// - 'failed': Max reconnection attempts reached

const useBuilderWebsocket = () => {
  const intl = useIntl();
  const [socket, setSocket] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isIntentionalCloseRef = useRef(false);
  const isMountedRef = useRef(true);
  const wasConnectedRef = useRef(false);
  const pingIntervalRef = useRef(null);

  const getWebSocketUrl = () => {
    return (
      (window.location.protocol === "https:" ? "wss://" : "ws://") +
      window.location.host +
      "/builder-backend/ws/builder"
    );
  };

  const connect = useCallback(() => {
    // Don't create new connection if one exists and is connecting/open
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      console.log("[Builder WS] Connection already exists, skipping");
      return;
    }

    // Don't reconnect if component is unmounted
    if (!isMountedRef.current) {
      return;
    }

    // Update status based on whether this is initial connect or reconnect
    if (reconnectAttemptsRef.current > 0) {
      setConnectionStatus("reconnecting");
    } else {
      setConnectionStatus("connecting");
    }

    console.log("[Builder WS] Connecting...");
    Cookie.set("pmtl_tk", authStore.getToken());

    const ws = new WebSocket(getWebSocketUrl());
    wsRef.current = ws;

    ws.onopen = function (event) {
      console.log("[Builder WS] Connected to: " + event.currentTarget.url);
      Cookie.remove("pmtl_tk", { path: "/" });
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      setReconnectAttempts(0);
      setIsReady(true);
      setSocket(ws);
      setConnectionStatus("connected");

      // Start ping interval to keep connection alive through proxies
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "PING" }));
        }
      }, PING_INTERVAL);
    };

    ws.onclose = (event) => {
      console.log("[Builder WS] Closed:", event.code, event.reason);
      setIsReady(false);
      setSocket(null);
      wsRef.current = null;

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Don't reconnect if it was an intentional close or component is unmounted
      if (isIntentionalCloseRef.current || !isMountedRef.current) {
        console.log("[Builder WS] Intentional close, not reconnecting");
        setConnectionStatus("disconnected");
        return;
      }

      // Attempt to reconnect with exponential backoff
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(
          INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
          MAX_RECONNECT_DELAY
        );

        reconnectAttemptsRef.current++;
        setReconnectAttempts(reconnectAttemptsRef.current);
        setConnectionStatus("reconnecting");

        console.log(
          `[Builder WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error("[Builder WS] Max reconnection attempts reached");
        setConnectionStatus("failed");
      }
    };

    ws.onerror = (error) => {
      console.error("[Builder WS] Error:", error);
      setIsReady(false);
    };
  }, []);

  // Manual retry function
  const retry = useCallback(() => {
    console.log("[Builder WS] Manual retry requested");
    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);
    isIntentionalCloseRef.current = false;

    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    connect();
  }, [connect]);

  useEffect(() => {
    isMountedRef.current = true;
    isIntentionalCloseRef.current = false;

    // Initial connection
    connect();

    return () => {
      isMountedRef.current = false;
      isIntentionalCloseRef.current = true;

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Close the WebSocket connection
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        console.log("[Builder WS] Closing connection on unmount");
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [connect]);

  // Track connection state changes
  useEffect(() => {
    if (isReady && !wasConnectedRef.current) {
      console.log("[Builder WS] Connection established");
      wasConnectedRef.current = true;
    } else if (isReady && wasConnectedRef.current) {
      console.log("[Builder WS] Reconnected");
    } else if (!isReady && wasConnectedRef.current) {
      console.log("[Builder WS] Connection lost");
    }
  }, [isReady, intl]);

  return {
    socket,
    isReady,
    connectionStatus,
    reconnectAttempts,
    maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
    retry,
  };
};

export default useBuilderWebsocket;
